/**
 * Daily Plan feed — latest plans at top.
 * Admins can create/edit/delete. Members can only read.
 *
 * Simplified form (Aug 2026): only Date + Title + Notes (rich text) + optional card background color.
 * Priority + Teams fields removed from the UI. Old data with priority/teams still renders in read view.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit3, Loader2, X, Calendar, MapPin, Users, Palette, Check,
  Copy, BookmarkPlus, Bookmark, ChevronDown, List as ListIcon, LayoutGrid, Share2, Link2, EyeOff, ArrowUpDown, Eye, Languages,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang } from "./i18n";
import DoserneShell from "./DoserneShell";
import RichTextEditor from "./RichTextEditor";
import RichContent from "./RichContent";
import { usePlanLang, FlagBar, FLAG_LABEL } from "./planTranslate";
import PlanPhotoPicker from "./PlanPhotoPicker";
import WeeklyPlanView from "./WeeklyPlanView";
import MaterialListModal from "./MaterialListModal";
import MaterialListSourcePicker from "./MaterialListSourcePicker";
import ProjectPicker from "./ProjectPicker";
import TeamsReorderModal from "./TeamsReorderModal";
import { findLastProjectIdBefore } from "./planEditorHelpers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Soft pastel palette for the card background. Empty string = default white.
const BG_COLORS = [
  { name: "White",  value: "" },
  { name: "Green",  value: "#dcfce7" },
  { name: "Orange", value: "#ffedd5" },
  { name: "Yellow", value: "#fef9c3" },
  { name: "Blue",   value: "#dbeafe" },
  { name: "Pink",   value: "#fce7f3" },
  { name: "Slate",  value: "#e2e8f0" },
];

function BackgroundPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest("[data-bg-picker]")) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const active = BG_COLORS.find((c) => c.value === (value || "")) || BG_COLORS[0];

  return (
    <div className="relative inline-block" data-bg-picker>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50"
        data-testid="plan-bg-toggle"
      >
        <div
          className="w-5 h-5 rounded border border-slate-300"
          style={{ backgroundColor: active.value || "#ffffff" }}
        />
        <Palette className="w-3.5 h-3.5" /> Culoare card
      </button>
      {open && (
        <div className="absolute z-30 top-9 left-0 bg-white border border-slate-200 rounded-lg shadow-lg p-2 flex gap-1.5">
          {BG_COLORS.map((c) => (
            <button
              key={c.name}
              type="button"
              onClick={() => { onChange(c.value); setOpen(false); }}
              title={c.name}
              className="w-8 h-8 rounded-lg border-2 border-slate-200 flex items-center justify-center hover:scale-110 transition"
              style={{ backgroundColor: c.value || "#ffffff" }}
              data-testid={`plan-bg-${c.name.toLowerCase()}`}
            >
              {(value || "") === c.value && <Check className="w-4 h-4 text-slate-900" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const DRAFT_KEY = "DOSERNE_PLAN_DRAFT_V1";

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    // Expire drafts older than 24h so a stale one doesn't haunt forever.
    if (!d?.saved_at || Date.now() - d.saved_at > 24 * 3600 * 1000) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return d;
  } catch { return null; }
}
function saveDraft(payload) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...payload, saved_at: Date.now() })); }
  catch { /* quota — ignore */ }
}
function clearDraft() { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } }

function PlanEditor({ initial, onCancel, onSaved, authHeader, onSaveAsTemplate, onOpenMaterialList, onOpenProject }) {
  const [form, setForm] = useState(initial || {
    plan_date: new Date().toISOString().slice(0, 10),
    title: "",
    body: "",
    background_color: "",
    // Keep legacy fields untouched for existing rows during edit.
    priority: "normal",
    teams: [],
  });
  const [busy, setBusy] = useState(false);
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const photoResolverRef = useRef(null);
  const projectResolverRef = useRef(null);
  // Project context captured AT THE MOMENT the user clicked the camera /
  // material-list toolbar button. Comes from `getNearestProjectId(editor)`
  // in RichTextEditor.jsx which uses the actual cursor position — not
  // "last project in the whole body". Without this, all photos ended up
  // cross-linked to whichever project happened to be last in the plan.
  const photoContextProjectRef = useRef("");
  const listContextProjectRef = useRef("");
  const bodyRef = useRef(form.body);
  useEffect(() => { bodyRef.current = form.body; }, [form.body]);

  // Auto-save the draft on every meaningful change so navigating away
  // (e.g. tapping a project pill inside the body) doesn't wipe the
  // in-progress plan. Restored automatically by the parent on mount.
  useEffect(() => {
    const hasContent = (form.title && form.title.trim()) || (form.body && form.body.trim());
    if (hasContent) saveDraft({ mode: form.id ? form.id : "new", form });
  }, [form]);

  // Count "Poza N" occurrences already inserted in the body to auto-suggest next N.
  const nextPhotoNumber = useMemo(() => {
    const re = /📷\s*Poza\s*(\d+)/g;
    let max = 0; let m;
    while ((m = re.exec(form.body || "")) !== null) {
      const n = parseInt(m[1], 10);
      if (n > max) max = n;
    }
    return max + 1;
  }, [form.body]);

  // Opens the picker and returns a promise that resolves when the user picks.
  // `contextProjectId` is the project id at the cursor position, captured by
  // RichTextEditor before the picker opens.
  const openPhotoPicker = (contextProjectId = "") =>
    new Promise((resolve) => {
      photoContextProjectRef.current = contextProjectId || "";
      photoResolverRef.current = resolve;
      setPhotoPickerOpen(true);
    });

  const handlePhotoPicked = (payload) => {
    setPhotoPickerOpen(false);
    if (photoResolverRef.current) {
      photoResolverRef.current(payload);
      photoResolverRef.current = null;
    }
    // Cross-link every picked photo to the project pill above the CURSOR
    // (captured by RichTextEditor at click-time), NOT the last project in
    // the whole plan. Fire-and-forget — used purely to populate the
    // project's photo history.
    try {
      const photos = Array.isArray(payload) ? payload : (payload ? [payload] : []);
      // Prefer the cursor-context id; fall back to a body scan only if
      // we somehow lost it (e.g. RTE hadn't mounted the new prop yet).
      const projectId = photoContextProjectRef.current
        || findLastProjectIdBefore(bodyRef.current || "");
      if (projectId && photos.length) {
        const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
        photos.forEach((ph) => {
          axios.post(
            `${API_URL}/doserne/plan-photo-refs`,
            {
              plan_id: form.id || "",
              project_id: projectId,
              url: ph.url,
              label: ph.label || "",
              plan_date: form.plan_date || "",
            },
            authHeader(),
          ).catch(() => { /* silent — history sync is best-effort */ });
        });
      }
      photoContextProjectRef.current = "";
    } catch { /* ignore */ }
  };
  const handlePhotoCancel = () => {
    setPhotoPickerOpen(false);
    photoContextProjectRef.current = "";
    if (photoResolverRef.current) {
      photoResolverRef.current(null);
      photoResolverRef.current = null;
    }
  };

  // Open the "Ny liste OR Fra kollega" picker. Resolves with a
  // `{id, title}` object when the user either creates a new list or
  // selects an existing progress-source one, or with `null` if they
  // cancel. RichTextEditor uses the resolved id to insert the anchor
  // pill into the plan body.
  const pickerResolverRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerProjectId, setPickerProjectId] = useState("");

  const createMaterialList = (contextProjectId = "") =>
    new Promise((resolve) => {
      // Use the cursor-based project id passed by RichTextEditor. Fall back
      // to the "last project in body" scan only when the RTE didn't provide
      // one (defensive — matches the photo picker's contract).
      const projectId = contextProjectId || findLastProjectIdBefore(bodyRef.current || "");
      pickerResolverRef.current = resolve;
      setPickerProjectId(projectId || "");
      setPickerOpen(true);
    });

  const handlePickerCreated = (list) => {
    setPickerOpen(false);
    setTimeout(() => onOpenMaterialList?.(list.id), 100);
    pickerResolverRef.current?.({ id: list.id, title: list.title });
    pickerResolverRef.current = null;
  };
  const handlePickerExisting = (list) => {
    setPickerOpen(false);
    // Piggy-back on the same anchor mechanism — no need to duplicate
    // items: the daily plan just links to the existing list id, and
    // the same MaterialListModal opens it (with author-only edit
    // guard now enforced on the backend).
    pickerResolverRef.current?.({ id: list.id, title: list.title });
    pickerResolverRef.current = null;
  };
  const handlePickerCancel = () => {
    setPickerOpen(false);
    pickerResolverRef.current?.(null);
    pickerResolverRef.current = null;
  };

  // Open the project picker and resolve with {id,number,name,address} when
  // the user picks something (or null if they close).
  const openProjectPicker = () =>
    new Promise((resolve) => {
      projectResolverRef.current = resolve;
      setProjectPickerOpen(true);
    });

  const handleProjectPicked = (payload) => {
    setProjectPickerOpen(false);
    if (projectResolverRef.current) {
      projectResolverRef.current(payload);
      projectResolverRef.current = null;
    }
  };
  const handleProjectCancel = () => {
    setProjectPickerOpen(false);
    if (projectResolverRef.current) {
      projectResolverRef.current(null);
      projectResolverRef.current = null;
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Tittel mangler"); return; }
    setBusy(true);
    try {
      const payload = {
        plan_date: form.plan_date,
        title: form.title,
        body: form.body || "",
        background_color: form.background_color || "",
        priority: form.priority || "normal",
        teams: form.teams || [],
      };
      if (form.id) await axios.put(`${API}/doserne/daily-plans/${form.id}`, payload, authHeader());
      else await axios.post(`${API}/doserne/daily-plans`, payload, authHeader());
      clearDraft();
      toast.success(form.id ? "Oppdatert" : "Publisert");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Feil"); }
    setBusy(false);
  };

  return (
    <div
      className="border-2 border-emerald-300 rounded-2xl p-5 sm:p-6 shadow-md space-y-4"
      style={{ backgroundColor: form.background_color || "#ffffff" }}
      data-testid="plan-editor"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black text-slate-900">{form.id ? "Rediger plan" : "Ny plan"}</h2>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-900 p-1"><X className="w-5 h-5" /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Dato</label>
          <input
            type="date" value={form.plan_date}
            onChange={(e) => setForm({ ...form, plan_date: e.target.value })}
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-slate-900 bg-white"
            data-testid="plan-date"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Tittel</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-slate-900 bg-white"
            placeholder="F.eks. Mandag 18. februar — 3 lag ute"
            data-testid="plan-title"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">Notater</label>
          <BackgroundPicker
            value={form.background_color}
            onChange={(v) => setForm({ ...form, background_color: v })}
          />
        </div>
        <RichTextEditor
          value={form.body}
          onChange={(html) => setForm({ ...form, body: html })}
          onAddPhoto={openPhotoPicker}
          onAddMaterialList={createMaterialList}
          onOpenMaterialList={onOpenMaterialList}
          onAddProject={openProjectPicker}
          onOpenProject={onOpenProject}
          placeholder="Ex: Ședință la 06:30. Bogdan ia echipa la Klofta. ⚠️ De adus 3 role plastic."
          minHeight={200}
          testid="plan-body-editor"
        />
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        <button
          type="button" onClick={save} disabled={busy}
          className="flex-1 min-w-[140px] py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 rounded-lg font-semibold text-sm shadow-sm flex items-center justify-center gap-2"
          style={{ color: "#fff" }} data-testid="save-plan-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (form.id ? "Oppdater" : "Publiser")}
        </button>
        <button
          type="button"
          onClick={() => setReorderOpen(true)}
          className="px-3 py-2 border border-sky-300 rounded-lg text-sm font-semibold text-sky-700 hover:bg-sky-50 bg-white inline-flex items-center gap-1.5"
          data-testid="reorder-teams-btn"
          title="Trage și mută echipe în sus/jos (pozele și listele se mută cu ele)"
        >
          <ArrowUpDown className="w-4 h-4" /> Reordonează echipe
        </button>
        {onSaveAsTemplate && (
          <button
            type="button"
            onClick={() => onSaveAsTemplate({ body: form.body, background_color: form.background_color })}
            className="px-3 py-2 border border-emerald-300 rounded-lg text-sm font-semibold text-emerald-700 hover:bg-emerald-50 bg-white inline-flex items-center gap-1.5"
            data-testid="save-as-template-btn"
          >
            <BookmarkPlus className="w-4 h-4" /> Salvează șablon
          </button>
        )}
        <button
          type="button" onClick={onCancel}
          className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50 bg-white"
        >
          Avbryt
        </button>
      </div>

      <PlanPhotoPicker
        open={photoPickerOpen}
        onClose={handlePhotoCancel}
        onPick={handlePhotoPicked}
        authHeader={authHeader}
        nextNumber={nextPhotoNumber}
      />

      <ProjectPicker
        open={projectPickerOpen}
        onClose={handleProjectCancel}
        onPick={handleProjectPicked}
        authHeader={authHeader}
      />

      <TeamsReorderModal
        open={reorderOpen}
        initialBody={form.body}
        onClose={() => setReorderOpen(false)}
        onSave={(newBody) => {
          setForm({ ...form, body: newBody });
          setReorderOpen(false);
          toast.success("Ordinea echipelor a fost salvată");
        }}
      />

      <MaterialListSourcePicker
        open={pickerOpen}
        projectId={pickerProjectId}
        authHeader={authHeader}
        onCancel={handlePickerCancel}
        onCreated={handlePickerCreated}
        onPickExisting={handlePickerExisting}
      />
    </div>
  );
}

/** Detect if a plan body is HTML (from the new editor) vs legacy plain text. */
function isHtml(str) {
  if (!str) return false;
  return /<\/?[a-z][\s\S]*>/i.test(str);
}

function PlanCard({ plan, isAdmin, onEdit, onDelete, onShare, authHeader, onOpenMaterialList, onOpenProject, projectAddresses = null, transLang = "", onTransLang = null }) {
  const bg = plan.background_color || "";
  const [tBody, setTBody] = React.useState(null);
  const [tBusy, setTBusy] = React.useState(false);
  const displayBody = transLang && tBody ? tBody : plan.body;
  const bodyIsHtml = isHtml(displayBody);
  const [busy, setBusy] = React.useState(false);
  const [isPublic, setIsPublic] = React.useState(!!plan.is_public);
  const [slug, setSlug] = React.useState(plan.share_slug || "");
  const [viewersOpen, setViewersOpen] = React.useState(false);
  const [viewers, setViewers] = React.useState(null);
  const [viewersLoading, setViewersLoading] = React.useState(false);

  // Record a view once per (plan, session). Uses sessionStorage so multiple
  // renders / re-mounts of the same plan within the same tab session don't
  // spam the endpoint. Admins do NOT record their own views (they're just
  // curators, not the target audience the boss wants to track).
  React.useEffect(() => {
    if (!plan.id || isAdmin) return;
    const key = `doserne.plan_viewed:${plan.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    axios
      .post(`${API}/doserne/daily-plans/${plan.id}/view`, {}, authHeader())
      .catch(() => sessionStorage.removeItem(key));
  }, [plan.id, isAdmin, authHeader]);

  const loadViewers = async () => {
    setViewersLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/daily-plans/${plan.id}/views`, authHeader());
      setViewers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste seere");
    }
    setViewersLoading(false);
  };

  const toggleViewers = () => {
    const next = !viewersOpen;
    setViewersOpen(next);
    if (next && viewers === null) loadViewers();
  };

  // Translate the plan details (body) on demand — project names stay intact.
  React.useEffect(() => {
    let alive = true;
    if (!transLang || !plan.body || !plan.body.trim()) { setTBody(null); return; }
    setTBusy(true);
    axios
      .post(`${API}/doserne/daily-plans/${plan.id}/translate`, { lang: transLang }, authHeader())
      .then(({ data }) => { if (alive) setTBody(data?.body || null); })
      .catch(() => { if (alive) setTBody(null); })
      .finally(() => { if (alive) setTBusy(false); });
    return () => { alive = false; };
  }, [transLang, plan.id, plan.body, authHeader]);

  const togglePublic = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/doserne/daily-plans/${plan.id}/public-share?enable=${!isPublic}`,
        {},
        authHeader(),
      );
      setIsPublic(data.is_public);
      setSlug(data.share_slug);
      if (data.is_public) {
        const url = `${window.location.origin}/p/plan/${data.share_slug}`;
        try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
        toast.success("Link public copiat în clipboard ✓");
        onShare && onShare(plan.id, data.share_slug, true);
      } else {
        toast.success("Share public dezactivat");
        onShare && onShare(plan.id, data.share_slug, false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oppdatere delingslenke");
    }
    setBusy(false);
  };

  const copyLink = async () => {
    if (!slug) return;
    const url = `${window.location.origin}/p/plan/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiat ✓");
    } catch {
      toast.error("Nu am putut copia (browser blocat)");
    }
  };

  return (
    <article
      className="border-2 border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm transition hover:shadow-md"
      style={{ backgroundColor: bg || "#ffffff" }}
      data-testid={`plan-${plan.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-mono text-slate-500">{plan.plan_date}</span>
            {isPublic && (
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 border border-emerald-500/30"
                    data-testid={`plan-public-badge-${plan.id}`}>
                <Link2 className="w-2.5 h-2.5"/> Public
              </span>
            )}
          </div>
          <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">{plan.title}</h3>
          {onTransLang && (
            <FlagBar value={transLang} onChange={onTransLang} className="mt-1.5" />
          )}
        </div>
        {isAdmin && (
          <div className="flex gap-1 flex-shrink-0 items-center">
            {isPublic && (
              <button onClick={copyLink}
                      className="text-emerald-500 hover:text-emerald-700 p-1"
                      title="Copiază link public"
                      data-testid={`copy-share-${plan.id}`}>
                <Link2 className="w-4 h-4"/>
              </button>
            )}
            <button onClick={togglePublic}
                    disabled={busy}
                    className={`p-1 ${isPublic ? "text-slate-400 hover:text-red-600" : "text-slate-400 hover:text-emerald-600"}`}
                    title={isPublic ? "Dezactivează share public" : "Generează link public"}
                    data-testid={`share-toggle-${plan.id}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : isPublic ? <EyeOff className="w-4 h-4"/> : <Share2 className="w-4 h-4"/>}
            </button>
            <button onClick={() => onEdit(plan)} className="text-slate-400 hover:text-slate-900 p-1" data-testid={`edit-${plan.id}`}><Edit3 className="w-4 h-4" /></button>
            <button onClick={() => onDelete(plan)} className="text-red-400 hover:text-red-700 p-1" data-testid={`delete-${plan.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      {transLang && (tBusy || tBody) && (
        <div className="inline-flex items-center gap-1.5 mb-2 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wide" data-testid={`plan-translated-${plan.id}`}>
          {tBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
          {FLAG_LABEL[transLang] || transLang}
        </div>
      )}

      {displayBody && (
        bodyIsHtml
          ? <RichContent html={displayBody} className="text-sm text-slate-700 mb-4" onOpenMaterialList={onOpenMaterialList} onOpenProject={onOpenProject} projectAddresses={projectAddresses} />
          : <p className="text-sm text-slate-600 whitespace-pre-wrap mb-4 leading-relaxed">{displayBody}</p>
      )}

      {/* Legacy teams block — still displayed for older plans that had teams. */}
      {plan.teams?.length > 0 && (
        <div className="space-y-2">
          {plan.teams.map((t, i) => (
            <div key={i} className="flex items-start gap-3 p-2.5 bg-white/60 border border-slate-200 rounded-lg">
              <Users className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                {t.location && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {t.location}</div>}
                {t.workers?.length > 0 && <div className="text-xs text-slate-600 mt-1">{t.workers.join(" · ")}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-200 flex items-center gap-3">
        <div className="text-[10px] text-slate-400 flex-1 truncate">
          Publisert av {plan.created_by_name || plan.created_by}
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={toggleViewers}
            className="inline-flex items-center gap-1 text-[11px] font-black text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-400 rounded-full px-2 py-1 transition"
            data-testid={`plan-viewers-toggle-${plan.id}`}
            title="Se hvem som har åpnet planen"
          >
            <Eye className="w-3 h-3" />
            {viewers ? `${viewers.total_viewers} sett` : "Sett av"}
            <ChevronDown className={`w-3 h-3 transition-transform ${viewersOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>

      {isAdmin && viewersOpen && (
        <div className="mt-3 border-t border-slate-200 pt-3" data-testid={`plan-viewers-list-${plan.id}`}>
          {viewersLoading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Laster inn seere…
            </div>
          )}
          {!viewersLoading && viewers && viewers.viewers.length === 0 && (
            <div className="text-xs text-slate-500 italic">Ingen har åpnet planen ennå.</div>
          )}
          {!viewersLoading && viewers && viewers.viewers.length > 0 && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {viewers.viewers.map((v) => (
                <div
                  key={v.id || `${v.user_id}-${v.plan_id}`}
                  className="flex items-center justify-between gap-3 text-xs bg-white/70 border border-slate-100 rounded-lg px-2.5 py-1.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-slate-900 truncate">{v.user_name || v.user_email}</div>
                    <div className="text-[10px] text-slate-500 truncate">{v.user_email}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Sist</div>
                    <div className="text-[11px] font-mono text-slate-700">
                      {v.last_viewed_at ? new Date(v.last_viewed_at).toLocaleString("no-NO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </div>
                  </div>
                  {v.view_count > 1 && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500/15 text-emerald-700 text-[10px] font-black">
                      ×{v.view_count}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function DoserneDailyPlan() {
  const { user, loading, authHeader } = useDoserneAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [projectAddresses, setProjectAddresses] = useState({});
  const [transLang, setTransLang] = usePlanLang(authHeader);
  const [busy, setBusy] = useState(true);
  // Restore any auto-saved draft (e.g. after the user accidentally
  // tapped a project pill in the editor and got navigated away).
  const [editing, setEditing] = useState(() => {
    const d = loadDraft();
    if (!d) return null;
    if (d.mode === "new") return { ...(d.form || {}), _restored: true };
    // Existing plan draft — load the plan object shape we normally pass.
    return { ...(d.form || {}), _restored: true };
  });
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("DOSERNE_PLAN_VIEW") || "feed"); // "feed" | "week"
  const [materialListId, setMaterialListId] = useState(null); // opens modal when set
  const templatesBtnRef = useRef(null);

  const openMaterialList = (listId) => setMaterialListId(listId);
  const closeMaterialList = () => setMaterialListId(null);
  const openProject = (projectId) => navigate(`/doserne/prosjekter/${projectId}?from_plan=today`);

  useEffect(() => { localStorage.setItem("DOSERNE_PLAN_VIEW", view); }, [view]);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/doserne/daily-plans?limit=100`, authHeader());
      setPlans(data.plans || []);
      setProjectAddresses(data.project_addresses || {});
    } catch { toast.error("Kunne ikke laste"); }
    setBusy(false);
  };

  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/plan-templates`, authHeader());
      setTemplates(data.templates || []);
    } catch { /* silent */ }
  };

  useEffect(() => { if (user) { load(); loadTemplates(); } }, [user]);

  // Notify the user that we recovered their draft. Fires once.
  useEffect(() => {
    if (editing && editing._restored) {
      toast.info("Planen din er gjenopprettet fra utkastet");
      // Strip the marker so subsequent state changes don't re-fire.
      setEditing((cur) => (cur && cur._restored) ? { ...cur, _restored: false } : cur);
    }
  }, []);  // intentionally run only on mount

  // Close templates dropdown on outside click.
  useEffect(() => {
    if (!templatesOpen) return;
    const onDoc = (e) => {
      if (templatesBtnRef.current && !templatesBtnRef.current.contains(e.target)) setTemplatesOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [templatesOpen]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  const isAdmin = !!user.is_admin;

  const deletePlan = async (plan) => {
    if (!window.confirm(`Slett plan "${plan.title}"?`)) return;
    try {
      await axios.delete(`${API}/doserne/daily-plans/${plan.id}`, authHeader());
      toast.success("Slettet");
      load();
    } catch { toast.error("Feil"); }
  };

  const copyLastPlan = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/daily-plans/latest`, authHeader());
      if (!data.plan) { toast.error("Nu există plan anterior"); return; }
      const p = data.plan;
      setEditing({
        // No id → creates a new plan on save
        plan_date: new Date().toISOString().slice(0, 10),
        title: p.title,
        body: p.body || "",
        background_color: p.background_color || "",
        priority: p.priority || "normal",
        teams: [], // Reset teams — legacy field, not used in new UX
      });
      toast.success("Copiat din ultimul plan");
    } catch { toast.error("Feil"); }
  };

  const applyTemplate = (tpl) => {
    setEditing({
      plan_date: new Date().toISOString().slice(0, 10),
      title: tpl.name,
      body: tpl.body || "",
      background_color: tpl.background_color || "",
      priority: "normal",
      teams: [],
    });
    setTemplatesOpen(false);
    toast.success(`Șablon "${tpl.name}" încărcat`);
  };

  const saveAsTemplate = async ({ body, background_color }) => {
    const name = window.prompt("Nume șablon:", "Șablon nou");
    if (!name || !name.trim()) return;
    try {
      const { data } = await axios.post(
        `${API}/doserne/plan-templates`,
        { name: name.trim(), body: body || "", background_color: background_color || "" },
        authHeader(),
      );
      setTemplates((prev) => [data, ...prev]);
      toast.success(`Șablon "${data.name}" salvat`);
    } catch { toast.error("Nu s-a putut salva șablonul"); }
  };

  const deleteTemplate = async (tpl, e) => {
    e.stopPropagation();
    if (!window.confirm(`Șterge șablonul "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/doserne/plan-templates/${tpl.id}`, authHeader());
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      toast.success("Șters");
    } catch { toast.error("Feil"); }
  };

  return (
    <DoserneShell title="Daglig plan" subtitle={isAdmin ? "Publiser og administrer dagens jobber" : "Se hva teamene skal gjøre i dag"}>
      {/* View toggle — everyone sees it */}
      {!editing && (
        <div className="mb-3 inline-flex p-1 rounded-lg border border-slate-200 bg-white gap-1" data-testid="plan-view-toggle">
          <button
            onClick={() => setView("feed")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition ${view === "feed" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            data-testid="view-feed-btn"
          >
            <ListIcon className="w-3.5 h-3.5" /> Feed
          </button>
          <button
            onClick={() => setView("week")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition ${view === "week" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            data-testid="view-week-btn"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Săptămână
          </button>
        </div>
      )}

      {isAdmin && !editing && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 rounded-lg text-sm font-semibold shadow-sm"
            style={{ color: "#fff" }}
            data-testid="new-plan-btn"
          >
            <Plus className="w-4 h-4" /> Ny plan
          </button>

          <button
            onClick={copyLastPlan}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-slate-300 bg-white rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="copy-last-plan-btn"
          >
            <Copy className="w-4 h-4" /> Copiază ultimul plan
          </button>

          <div className="relative" ref={templatesBtnRef}>
            <button
              onClick={() => setTemplatesOpen((s) => !s)}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-slate-300 bg-white rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="templates-toggle-btn"
            >
              <Bookmark className="w-4 h-4" /> Șabloane
              {templates.length > 0 && (
                <span className="ml-1 text-[10px] font-black bg-emerald-100 text-emerald-800 rounded-full px-1.5 py-0.5">{templates.length}</span>
              )}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {templatesOpen && (
              <div className="absolute z-20 top-12 left-0 min-w-[260px] bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden" data-testid="templates-menu">
                {templates.length === 0 ? (
                  <div className="px-4 py-6 text-center text-xs text-slate-500">
                    Niciun șablon salvat.
                    <div className="mt-1 text-slate-400">Salvează unul din editor.</div>
                  </div>
                ) : (
                  <ul className="max-h-72 overflow-y-auto py-1">
                    {templates.map((tpl) => (
                      <li key={tpl.id} className="flex items-center gap-1 px-1 hover:bg-slate-50 transition">
                        <button
                          type="button"
                          onClick={() => applyTemplate(tpl)}
                          className="flex-1 min-w-0 flex items-center gap-3 px-2 py-2 text-left"
                          data-testid={`apply-template-${tpl.id}`}
                        >
                          <div
                            className="w-4 h-4 rounded border border-slate-300 flex-shrink-0"
                            style={{ backgroundColor: tpl.background_color || "#ffffff" }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">{tpl.name}</div>
                            <div className="text-[10px] text-slate-400 truncate">
                              {tpl.created_by_name || tpl.created_by}
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => deleteTemplate(tpl, e)}
                          className="text-slate-300 hover:text-red-500 p-2 flex-shrink-0"
                          data-testid={`delete-template-${tpl.id}`}
                          title="Șterge șablon"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="space-y-4">
        {editing && (
          <PlanEditor
            initial={editing === "new" ? null : editing}
            onCancel={() => { clearDraft(); setEditing(null); }}
            onSaved={() => { setEditing(null); load(); }}
            authHeader={authHeader}
            onSaveAsTemplate={saveAsTemplate}
            onOpenMaterialList={openMaterialList}
            onOpenProject={openProject}
          />
        )}
        {!editing && view === "week" ? (
          <WeeklyPlanView
            authHeader={authHeader}
            isAdmin={isAdmin}
            lang={lang}
            onEdit={(p) => setEditing(p)}
            onNew={(iso) => setEditing({
              plan_date: iso,
              title: "",
              body: "",
              background_color: "",
              priority: "normal",
              teams: [],
            })}
          />
        ) : busy ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : plans.length === 0 ? (
          <div className="text-center text-slate-400 italic py-16">
            <Calendar className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            Ingen plan publisert ennå.
            {isAdmin && <p className="mt-2 text-slate-500">Trykk &quot;Ny plan&quot; for å publisere den første.</p>}
          </div>
        ) : (
          plans.map((p) => <PlanCard key={p.id} plan={p} isAdmin={isAdmin} onEdit={setEditing} onDelete={deletePlan} authHeader={authHeader} onOpenMaterialList={openMaterialList} onOpenProject={openProject} projectAddresses={projectAddresses} transLang={transLang} onTransLang={setTransLang} />)
        )}
      </div>

      {materialListId && (
        <MaterialListModal
          listId={materialListId}
          authHeader={authHeader}
          canDelete={isAdmin}
          onClose={closeMaterialList}
          onDeleted={closeMaterialList}
        />
      )}
    </DoserneShell>
  );
}
